import React, { FC, ChangeEvent, useEffect, useCallback, useContext } from 'react';
import Input from '@bankiru/ui-kit/core/Input';
import Flexbox from '@bankiru/ui-kit/core/Flexbox';
import Text from '@bankiru/ui-kit/core/Text';
import { useSplittedInput } from './hooks';
import { SMS_ERROR_CODES } from './constants';
import { ThemeContext } from '../../../../../context/ThemeContextWrapper';

type SplittedCodeInputProps = {
	isDisabled: boolean;
	onChange: (value: string) => void;
	error?: string;
	value: string;
};

const CODE_LENGTH = 4;

export const SplittedCodeInput: FC<SplittedCodeInputProps> = ({
	isDisabled,
	onChange,
	error,
	value,
}) => {
	const { isMobile } = useContext(ThemeContext);

	const {
		inputRefs,
		containerRef,
		updatePositionOnChange,
		updatePositionOnKeydown,
		focusedInputs,
		updateOnBlur,
		updateOnFocus,
	} = useSplittedInput();

	useEffect(() => {
		if (error === SMS_ERROR_CODES.WRONG_CODE) {
			inputRefs.current.forEach(item => {
				if (!item) {
					return;
				}

				item.value = '';
			});

			// не работает показ клавиатуры на айфонах
			if (!isMobile) {
				inputRefs.current[0]?.focus();
			}
		}
	}, [error, inputRefs, isMobile]);

	const handleInputChange = (
		event: ChangeEvent<HTMLInputElement>,
		inputValue: string | number | undefined,
		index: number,
	) => {
		const fieldValues = inputRefs.current.map(el => el?.value ?? null);
		const newValue = fieldValues.join('');
		const focusedInputIndex = focusedInputs.findIndex(isFocused => isFocused);

		// если ничего не изменилось - позицию не меняем
		if (newValue === value && !inputRefs.current[focusedInputIndex]?.value) {
			return;
		}

		updatePositionOnChange(event, inputValue, index);

		onChange(fieldValues?.join('') ?? '');
	};

	// обработка вставки из буфера обмена
	const checkPaste = useCallback(
		(event: Event) => {
			const eventTarget = (event as InputEvent).currentTarget as HTMLInputElement;

			const targetValue = eventTarget?.value;
			if (targetValue && targetValue.length === CODE_LENGTH) {
				inputRefs.current.forEach((currentInput, index) => {
					if (!currentInput) {
						return;
					}

					currentInput.value = targetValue[index];
				});

				onChange(targetValue);
			}
		},
		[inputRefs, onChange],
	);

	useEffect(() => {
		const inputs = inputRefs.current;

		inputs.forEach(currentInput => {
			currentInput?.addEventListener('input', checkPaste);
		});

		return () => {
			inputs.forEach(currentInput => {
				currentInput?.removeEventListener('input', checkPaste);
			});
		};
	}, [checkPaste, inputRefs]);

	// todo невозможно нормально использовать рефы с текущим MaskedInputом - поэтому рефы набиваем в эффекте
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const inputs = containerRef.current.querySelectorAll('input');
		inputRefs.current = inputRefs.current.map((_, index: number) => inputs[index]);

		// todo ui-kit: автофокус через пропсы не работает для маскед инпутов
		if (inputRefs.current[0]) {
			inputRefs.current[0].autofocus = true;
		}
	}, [containerRef, inputRefs]);

	return (
		<Flexbox flexDirection="column" gap="8">
			<Flexbox flexDirection="row" gap="8" justifyContent="center">
				<div className="splittedInputWrapper" ref={containerRef}>
					{[...Array(4)].map((_, index) => (
						<div className="splittedInputContainer" key={`inputCode_${index}`}>
							<Input
								onBlur={() => updateOnBlur(index)}
								onFocus={() => updateOnFocus(index)}
								onChange={(event, value) => handleInputChange(event, value, index)}
								onKeyDown={event => updatePositionOnKeydown(event, index)}
								placeholder={focusedInputs[index] ? undefined : '-'}
								type="tel"
								inputId="test"
								autoComplete="one-time-code"
								mask={[/\d/]}
								autoFocus={index === 0 && !isMobile} // не работает показ клавиатуры на айфонах
								isMaskGuideEnabled={true}
								isDisabled={isDisabled}
								status={error ? 'error' : 'default'}
								color={error ? 'majorRed' : undefined}
							/>
						</div>
					))}
				</div>
			</Flexbox>
			{error && (
				<Text color="destructive" size="fs12">
					{error}
				</Text>
			)}
		</Flexbox>
	);
};
